import React, { useEffect } from 'react';
import { Container, Row, Col, Card, Badge } from 'react-bootstrap';
import { useWorks } from '../hooks/useApi';
import SEOHead from '../components/SEOHead';
import { seoData } from '../data/seoData';

const OurWorkPage: React.FC = () => {
  const { works, loading, error, fetchWorks } = useWorks();

  useEffect(() => {
    fetchWorks();
  }, []);

  if (loading) {
    return (
      <>
        <SEOHead {...seoData.work} page="work" />
        <Container className="py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SEOHead {...seoData.work} page="work" />
        <Container className="py-5">
          <div className="text-center text-danger">
            <p>Error loading our work: {error}</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <SEOHead {...seoData.work} page="work" />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-5">
        <Container>
          <div className="text-center">
            <h1 className="display-4 fw-bold mb-3">Our Work Portfolio</h1>
            <p className="lead">
              See the quality of our work through our completed projects and satisfied customers
            </p>
          </div>
        </Container>
      </section>

      {/* Work Gallery */}
      <section className="py-5">
        <Container>
          <Row>
            {works.map((work) => (
              <Col lg={4} md={6} className="mb-4" key={work.id}>
                <Card className="h-100 shadow-sm border-0">
                  {work.photos && work.photos.length > 0 && (
                    <Card.Img 
                      variant="top" 
                      src={work.photos[0]} 
                      alt={work.title}
                      style={{ height: '200px', objectFit: 'cover' }}
                    />
                  )}
                  <Card.Body className="d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Badge bg="primary" className="mb-2">
                        {work.category}
                      </Badge>
                      {work.is_featured && (
                        <Badge bg="warning" text="dark">
                          ⭐ Featured
                        </Badge>
                      )}
                    </div>
                    
                    <h5 className="card-title">{work.title}</h5>
                    <p className="card-text text-muted flex-grow-1">
                      {work.description}
                    </p>
                    
                    <div className="mt-auto">
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          📅 {new Date(work.date).toLocaleDateString()}
                        </small>
                        {work.photos && work.photos.length > 1 && (
                          <small className="text-muted">
                            📸 {work.photos.length} photos
                          </small>
                        )}
                      </div>
                      
                      {work.tags && work.tags.length > 0 && (
                        <div className="mt-2">
                          {work.tags.map((tag, index) => (
                            <Badge 
                              key={index} 
                              bg="light" 
                              text="dark" 
                              className="me-1 mb-1"
                            >
                              #{tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          
          {works.length === 0 && (
            <div className="text-center py-5">
              <h3 className="text-muted">No work samples available yet</h3>
              <p className="text-muted">Check back soon for our latest projects!</p>
            </div>
          )}
        </Container>
      </section>
    </>
  );
};

export default OurWorkPage;